import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, Search, Trash2, Pause, Play, RotateCcw,
  Loader2, Users, UserPlus, ChevronDown, MoreVertical, CheckSquare, Square, X, FolderPlus, Folder, FolderOpen, ChevronLeft, ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getSequenceEnrollments, removeEnrollmentFromSequence, resetEnrollment, bulkRemoveEnrollments, enrollRecipientGroups, getRecipientGroups, removeRecipientGroupFromSequence } from '../../services/api'
import EnrollRecipientsModal from './EnrollRecipientsModal'

// Numbered Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  const getVisiblePages = () => {
    const pages = []
    const maxVisible = 7
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('...')
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i)
      }
      if (currentPage < totalPages - 2) pages.push('...')
      if (!pages.includes(totalPages)) pages.push(totalPages)
    }
    return pages
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginTop: '1rem' }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '0.4rem',
          background: currentPage === 1 ? 'transparent' : 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '6px',
          color: currentPage === 1 ? '#555' : '#ccc',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
        }}
      >
        <ChevronLeft size={16} />
      </button>
      
      {getVisiblePages().map((page, idx) => (
        page === '...' ? (
          <span key={`ellipsis-${idx}`} style={{ padding: '0 0.5rem', color: '#666' }}>...</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              padding: '0.4rem 0.7rem',
              background: page === currentPage ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.05)',
              border: page === currentPage ? '1px solid rgba(91, 92, 244,0.4)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: page === currentPage ? '#5b5cf4' : '#ccc',
              cursor: 'pointer',
              fontWeight: page === currentPage ? 600 : 400,
              fontSize: '0.85rem'
            }}
          >
            {page}
          </button>
        )
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '0.4rem',
          background: currentPage === totalPages ? 'transparent' : 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '6px',
          color: currentPage === totalPages ? '#555' : '#ccc',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
        }}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

const STATUS_STYLES = {
  pending: { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af', label: 'En attente' },
  active: { bg: 'rgba(16,185,129,0.15)', color: '#10b981', label: 'Actif' },
  paused: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Pause' },
  completed: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', label: 'Termine' },
  exited: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'Sorti' }
}

export default function SequenceRecipientsTab({ sequence }) {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [showEnrollModal, setShowEnrollModal] = useState(false)
  const [showEnrollGroupsModal, setShowEnrollGroupsModal] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null) // null = all, object = specific group
  const limit = 25

  // Fetch enrollments (recipients only)
  const { data, isLoading, error } = useQuery({
    queryKey: ['sequenceEnrollments', sequence.id, 'recipients', page, searchQuery, statusFilter],
    queryFn: () => getSequenceEnrollments(sequence.id, {
      page,
      limit,
      q: searchQuery || undefined,
      status: statusFilter || undefined,
      recipientOnly: true
    }),
    enabled: !!sequence?.id
  })

  // Fetch groups for filter
  const { data: groupsData } = useQuery({
    queryKey: ['recipientGroups'],
    queryFn: getRecipientGroups
  })

  const groups = groupsData?.member || groupsData?.['hydra:member'] || groupsData || []

  // Filter enrollments by group if selected
  const allEnrollments = data?.items || data?.enrollments || data?.member || data?.['hydra:member'] || []
  const enrollments = selectedGroupFilter
    ? allEnrollments.filter(e => 
        e.recipient?.groupNames?.some(g => g.id === selectedGroupFilter.id)
      )
    : allEnrollments
  const totalFromApi = data?.total || data?.totalItems || data?.['hydra:totalItems'] || allEnrollments.length
  // When a group filter is selected, show the group's total member count, not just the filtered page count
  const total = selectedGroupFilter 
    ? (selectedGroupFilter.recipientCount ?? selectedGroupFilter.recipients?.length ?? enrollments.length)
    : totalFromApi
  const totalPages = Math.ceil(totalFromApi / limit)

  // Remove enrollment mutation
  const removeMutation = useMutation({
    mutationFn: (enrollmentId) => removeEnrollmentFromSequence(sequence.id, enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      toast.success('Destinataire retire de la sequence')
    },
    onError: (err) => toast.error(err.message || 'Erreur')
  })

  // Reset enrollment mutation
  const resetMutation = useMutation({
    mutationFn: resetEnrollment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      toast.success('Progression reinitialised')
    },
    onError: (err) => toast.error(err.message || 'Erreur')
  })

  // Bulk remove mutation
  const bulkRemoveMutation = useMutation({
    mutationFn: (enrollmentIds) => bulkRemoveEnrollments(sequence.id, enrollmentIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      setSelectedIds(new Set())
      toast.success(`${data.removed || selectedIds.size} destinataire(s) retire(s)`, { id: 'bulk-remove' })
    },
    onError: (err) => toast.error(err.message || 'Erreur lors de la suppression', { id: 'bulk-remove-error' })
  })

  // Remove all enrollments from a group
  const removeGroupMutation = useMutation({
    mutationFn: (groupId) => removeRecipientGroupFromSequence(sequence.id, groupId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      if (selectedGroupFilter?.id === data.groupId) {
        setSelectedGroupFilter(null)
      }
      toast.success(`${data.removed} destinataire(s) du groupe "${data.groupName}" retiré(s) de la séquence`)
    },
    onError: (err) => toast.error(err.response?.data?.message || err.message || 'Erreur lors de la suppression')
  })

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === enrollments.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(enrollments.map(e => e.id)))
    }
  }

  const clearSelection = () => setSelectedIds(new Set())

  const selectGroupFilter = (group) => {
    setSelectedGroupFilter(group)
    setPage(1)
  }

  const clearGroupFilter = () => {
    setSelectedGroupFilter(null)
    setPage(1)
  }

  const handleRemoveGroup = (group) => {
    if (confirm(`Retirer tous les destinataires du groupe "${group.name}" de cette séquence ?`)) {
      removeGroupMutation.mutate(group.id)
    }
  }

  const handleBulkRemove = () => {
    if (selectedIds.size === 0) return
    if (confirm(`Retirer ${selectedIds.size} destinataire(s) de la sequence ?`)) {
      bulkRemoveMutation.mutate(Array.from(selectedIds))
    }
  }

  const handleRemove = (enrollment) => {
    if (confirm('Retirer ce destinataire de la sequence ?')) {
      removeMutation.mutate(enrollment.id)
    }
    setActiveDropdown(null)
  }

  const handleReset = (enrollment) => {
    if (confirm('Reinitialiser la progression de ce destinataire ?')) {
      resetMutation.mutate(enrollment.id)
    }
    setActiveDropdown(null)
  }

  const getStatusBadge = (status) => {
    const s = STATUS_STYLES[status] || STATUS_STYLES.pending
    return (
      <span style={{
        padding: '0.2rem 0.6rem',
        background: s.bg,
        color: s.color,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500
      }}>
        {s.label}
      </span>
    )
  }

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Header with actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        {/* Search & Filter */}
        <div style={{ display: 'flex', gap: '0.75rem', flex: 1, maxWidth: '500px' }}>
          <div style={{
            flex: 1,
            position: 'relative'
          }}>
            <Search size={16} style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666'
            }} />
            <input
              type="text"
              placeholder="Rechercher par email ou nom..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '0.6rem 0.75rem 0.6rem 2.25rem',
                color: '#fff',
                fontSize: '0.9rem'
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '0.6rem 0.75rem',
              color: '#fff',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="active">Actif</option>
            <option value="paused">Pause</option>
            <option value="completed">Termine</option>
            <option value="exited">Sorti</option>
          </select>
        </div>

        {/* Add buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowEnrollModal(true)}
            style={{
              background: '#5b5cf4',
              color: '#fff',
              border: 'none',
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            <UserPlus size={16} />
            Ajouter des destinataires
          </button>

          <button
            onClick={() => setShowEnrollGroupsModal(true)}
            style={{
              background: 'rgba(59,130,246,0.15)',
              border: '1px solid rgba(59,130,246,0.35)',
              color: '#3b82f6',
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            <FolderPlus size={16} />
            Inscrire des groupes
          </button>
        </div>
      </div>

      {/* Group Filter Section */}
      {groups.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={clearGroupFilter}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.75rem',
                background: !selectedGroupFilter ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.03)',
                border: !selectedGroupFilter ? '1px solid rgba(91, 92, 244,0.3)' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: !selectedGroupFilter ? '#5b5cf4' : '#888',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              <Users size={14} />
              Tous
            </button>
            {groups.map((group) => {
              const isActive = selectedGroupFilter?.id === group.id
              return (
                <div key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <button
                    onClick={() => isActive ? clearGroupFilter() : selectGroupFilter(group)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.4rem 0.75rem',
                      background: isActive ? `${group.color}20` : 'rgba(255,255,255,0.03)',
                      border: isActive ? `1px solid ${group.color}50` : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '6px 0 0 6px',
                      color: isActive ? group.color : '#888',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      transition: 'all 0.15s'
                    }}
                  >
                    {isActive ? <FolderOpen size={14} /> : <Folder size={14} />}
                    {group.name}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveGroup(group)
                    }}
                    disabled={removeGroupMutation.isPending}
                    title={`Retirer tous les membres de "${group.name}" de la séquence`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.4rem 0.5rem',
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      borderLeft: 'none',
                      borderRadius: '0 6px 6px 0',
                      color: '#ef4444',
                      cursor: removeGroupMutation.isPending ? 'wait' : 'pointer',
                      fontSize: '0.8rem',
                      transition: 'all 0.15s'
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Stats / Bulk Action Toolbar */}
      {selectedIds.size > 0 ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px'
        }}>
          <span style={{ fontWeight: 500, color: '#fff' }}>
            {selectedIds.size} selectionne(s)
          </span>
          <button
            onClick={handleBulkRemove}
            disabled={bulkRemoveMutation.isPending}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              cursor: bulkRemoveMutation.isPending ? 'wait' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500
            }}
          >
            {bulkRemoveMutation.isPending ? (
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Trash2 size={14} />
            )}
            Retirer de la sequence
          </button>
          <button
            onClick={clearSelection}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#888',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            <X size={14} />
            Annuler
          </button>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem',
          color: '#888',
          fontSize: '0.85rem'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Users size={14} />
            {total} destinataire(s)
          </span>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
          Erreur: {error.message}
        </div>
      ) : enrollments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Mail size={40} style={{ color: '#666', marginBottom: '1rem' }} />
          <p style={{ color: '#888', marginBottom: '1rem' }}>
            {searchQuery || statusFilter
              ? 'Aucun destinataire trouve avec ces criteres'
              : 'Aucun destinataire inscrit a cette sequence'}
          </p>
          {!searchQuery && !statusFilter && (
            <button
              onClick={() => setShowEnrollModal(true)}
              style={{
                background: '#5b5cf4',
                color: '#fff',
                border: 'none',
                padding: '0.6rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              <UserPlus size={14} style={{ display: 'inline', marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Ajouter des destinataires
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 80px',
              gap: '1rem',
              padding: '0.75rem 1rem',
              background: 'rgba(255,255,255,0.03)',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#888',
              textTransform: 'uppercase',
              alignItems: 'center'
            }}>
              <div
                onClick={toggleSelectAll}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {selectedIds.size === enrollments.length && enrollments.length > 0 ? (
                  <CheckSquare size={18} style={{ color: '#5b5cf4' }} />
                ) : selectedIds.size > 0 ? (
                  <CheckSquare size={18} style={{ color: '#666' }} />
                ) : (
                  <Square size={18} />
                )}
              </div>
              <div>Destinataire</div>
              <div>Statut</div>
              <div>Progression</div>
              <div>Emails</div>
              <div></div>
            </div>

            {/* Rows */}
            {enrollments.map((enrollment) => {
              const recipient = enrollment.recipient
              const email = recipient?.email || ''
              const name = recipient?.fullName
                || [recipient?.firstName, recipient?.lastName].filter(Boolean).join(' ')
                || email
              const isSelected = selectedIds.has(enrollment.id)

              return (
                <div
                  key={enrollment.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 80px',
                    gap: '1rem',
                    padding: '0.875rem 1rem',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    alignItems: 'center',
                    background: isSelected ? 'rgba(91, 92, 244,0.05)' : 'transparent'
                  }}
                >
                  {/* Checkbox */}
                  <div
                    onClick={() => toggleSelect(enrollment.id)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    {isSelected ? (
                      <CheckSquare size={18} style={{ color: '#5b5cf4' }} />
                    ) : (
                      <Square size={18} style={{ color: '#666' }} />
                    )}
                  </div>

                  {/* Destinataire */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'rgba(91, 92, 244,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#5b5cf4',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{name}</div>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>{email}</div>
                      {recipient?.groupNames && recipient.groupNames.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                          {recipient.groupNames.map((group) => (
                            <span
                              key={group.id}
                              style={{
                                display: 'inline-block',
                                padding: '0.1rem 0.4rem',
                                background: `${group.color}15`,
                                border: `1px solid ${group.color}40`,
                                color: group.color,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 500
                              }}
                            >
                              {group.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Statut */}
                  <div>{getStatusBadge(enrollment.status)}</div>

                  {/* Progression */}
                  <div style={{ fontSize: '0.85rem', color: '#888' }}>
                    Etape {enrollment.currentStep}
                  </div>

                  {/* Emails */}
                  <div style={{ fontSize: '0.85rem' }}>
                    <div style={{ color: '#fff' }}>{enrollment.emailsSent} envoye(s)</div>
                    <div style={{ color: '#666', fontSize: '0.75rem' }}>
                      {enrollment.emailsOpened} ouvert(s)
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === enrollment.id ? null : enrollment.id)}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.1)',
                        padding: '0.4rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: '#888'
                      }}
                    >
                      <MoreVertical size={14} />
                    </button>

                    {activeDropdown === enrollment.id && (
                      <div style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        marginTop: '0.25rem',
                        background: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '0.5rem',
                        minWidth: '140px',
                        zIndex: 100
                      }}>
                        <button
                          onClick={() => handleReset(enrollment)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            width: '100%',
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            color: '#f59e0b',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            borderRadius: '4px',
                            textAlign: 'left'
                          }}
                        >
                          <RotateCcw size={14} />
                          Reinitialiser
                        </button>
                        <button
                          onClick={() => handleRemove(enrollment)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            width: '100%',
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            borderRadius: '4px',
                            textAlign: 'left'
                          }}
                        >
                          <Trash2 size={14} />
                          Retirer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Numbered Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && (
        <EnrollRecipientsModal
          sequence={sequence}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
          }}
        />
      )}

      {/* Enroll Groups Modal */}
      {showEnrollGroupsModal && (
        <EnrollGroupsModal
          sequence={sequence}
          onClose={() => setShowEnrollGroupsModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
          }}
        />
      )}
    </div>
  )
}

// Modal for enrolling recipient groups
function EnrollGroupsModal({ sequence, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set())
  const [forceReset, setForceReset] = useState(false)

  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['recipientGroups'],
    queryFn: getRecipientGroups
  })

  const groups = groupsData?.member || groupsData?.['hydra:member'] || groupsData || []

  const mutation = useMutation({
    mutationFn: () => enrollRecipientGroups(sequence.id, Array.from(selectedGroupIds), forceReset),
    onSuccess: (data) => {
      toast.success(`${data.enrolled} destinataire(s) inscrit(s) depuis ${selectedGroupIds.size} groupe(s)`, { id: 'enroll-groups' })
      onSuccess?.()
      onClose()
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de l\'inscription', { id: 'enroll-groups-error' })
    }
  })

  const toggleGroup = (groupId) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedGroupIds.size === groups.length) {
      setSelectedGroupIds(new Set())
    } else {
      setSelectedGroupIds(new Set(groups.map(g => g.id)))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (selectedGroupIds.size === 0) {
      toast.error('Aucun groupe sélectionné')
      return
    }
    mutation.mutate()
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '14px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '560px',
        border: '1px solid rgba(255,255,255,0.1)',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                Inscrire des groupes
              </h3>
              <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Séquence : {sequence.name}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Select all */}
          {groups.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '8px'
            }}>
              <button
                type="button"
                onClick={toggleSelectAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {selectedGroupIds.size === groups.length && groups.length > 0 ? (
                  <CheckSquare size={18} style={{ color: '#3b82f6' }} />
                ) : selectedGroupIds.size > 0 ? (
                  <CheckSquare size={18} style={{ color: '#666' }} />
                ) : (
                  <Square size={18} style={{ color: '#666' }} />
                )}
              </button>
              <span style={{ fontSize: '0.9rem', color: '#ccc', fontWeight: 500 }}>
                Tout sélectionner ({selectedGroupIds.size}/{groups.length})
              </span>
            </div>
          )}

          {/* Groups list */}
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : groups.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '8px',
              color: '#888'
            }}>
              <FolderPlus size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p>Aucun groupe de destinataires</p>
            </div>
          ) : (
            <div style={{ marginBottom: '1.5rem', maxHeight: '300px', overflowY: 'auto' }}>
              {groups.map((group) => {
                const isSelected = selectedGroupIds.has(group.id)
                return (
                  <div
                    key={group.id}
                    onClick={() => toggleGroup(group.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: isSelected ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.02)',
                      border: isSelected ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {isSelected ? (
                      <CheckSquare size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
                    ) : (
                      <Square size={18} style={{ color: '#666', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        marginBottom: '0.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>{group.name}</span>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.1rem 0.4rem',
                            background: `${group.color}15`,
                            border: `1px solid ${group.color}40`,
                            color: group.color,
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        >
                          {group.recipientCount || 0}
                        </span>
                      </div>
                      {group.description && (
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                          {group.description}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Force reset */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: '#ccc'
          }}>
            <input
              type="checkbox"
              checked={forceReset}
              onChange={(e) => setForceReset(e.target.checked)}
            />
            Forcer le redémarrage si déjà inscrit
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#ccc',
                padding: '0.65rem 1.25rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || selectedGroupIds.size === 0}
              style={{
                background: selectedGroupIds.size === 0 ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.15)',
                border: `1px solid ${selectedGroupIds.size === 0 ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.35)'}`,
                color: selectedGroupIds.size === 0 ? '#666' : '#3b82f6',
                padding: '0.65rem 1.25rem',
                borderRadius: '8px',
                cursor: selectedGroupIds.size === 0 || mutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Inscription...
                </>
              ) : (
                <>
                  <FolderPlus size={16} />
                  Inscrire {selectedGroupIds.size > 0 ? `${selectedGroupIds.size} groupe(s)` : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
